import { ArrowCounterClockwiseIcon } from "@phosphor-icons/react/dist/csr/ArrowCounterClockwise";
import { DotsThreeIcon } from "@phosphor-icons/react/dist/csr/DotsThree";
import { Button } from "@volca/ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { dayjs } from "@/core/utils/date";
import type { ICellComment, ICellCommentThread } from "./types";

// Marks menus portaled out of the comment card, so the card can tell a click
// inside one of them from a click away from it.
export const CELL_COMMENT_OVERLAY_ATTRIBUTE = "data-cell-comment-overlay";

export const isInsideCellCommentOverlay = (target: EventTarget | null) =>
  target instanceof Element &&
  !!target.closest(`[${CELL_COMMENT_OVERLAY_ATTRIBUTE}]`);

const commentTime = (at: string) => {
  const time = dayjs(at);
  return time.isSame(dayjs(), "year")
    ? time.format("MMM D, h:mm A")
    : time.format("MMM D, YYYY, h:mm A");
};

// Destructive and rare, so it waits behind a hover-revealed "more" control
// instead of sitting under every comment. Still reachable by keyboard: the
// control shows on focus too.
const CommentActions = ({ onDelete }: { onDelete: () => void }) => (
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger
      render={
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label="More options"
          className="text-subtle opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-popup-open:opacity-100"
        />
      }
    >
      <DotsThreeIcon className="size-4" aria-hidden />
    </DropdownMenuTrigger>
    <DropdownMenuContent
      align="end"
      className="min-w-max"
      {...{ [CELL_COMMENT_OVERLAY_ATTRIBUTE]: "" }}
    >
      <DropdownMenuItem variant="destructive" onClick={onDelete}>
        Delete comment
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const CommentItem = ({
  comment,
  onDelete,
}: {
  comment: ICellComment;
  onDelete: () => void;
}) => (
  <Stack gap={0} align="stretch" className="group w-full min-w-0">
    <Stack className="w-full" direction="row" align="center" gap={2}>
      <Text
        variant="label"
        className="min-w-0 flex-1 truncate"
        title={comment.author.full_name}
      >
        {comment.author.full_name}
      </Text>
      <Text variant="caption" tone="muted" className="shrink-0">
        {commentTime(comment.created_at)}
      </Text>
      {comment.mine && !comment.deleted ? (
        <CommentActions onDelete={onDelete} />
      ) : (
        <span aria-hidden className="size-6 shrink-0" />
      )}
    </Stack>
    {comment.deleted ? (
      <Text variant="caption" tone="muted" className="italic">
        Comment deleted
      </Text>
    ) : (
      <Text className="pr-8 leading-relaxed wrap-break-word whitespace-pre-wrap">
        {comment.body}
      </Text>
    )}
  </Stack>
);

/**
 * One thread's comments, oldest first. A resolved thread reads dimmed and says
 * who closed it, with the way to reopen it; resolving an open one lives with
 * the surface's own header, not here.
 */
export const CellCommentThreadView = ({
  thread,
  isUpdating,
  onDeleteComment,
  onReopen,
}: {
  thread: ICellCommentThread;
  isUpdating?: boolean;
  onDeleteComment: (commentId: string) => void;
  onReopen: () => void;
}) => (
  <Stack className="w-full" gap={2} align="stretch">
    <Stack
      gap={2}
      align="stretch"
      className={cn("w-full", { "opacity-60": !!thread.resolved_at })}
    >
      {thread.comments.map((comment) => (
        <CommentItem
          key={comment.id}
          comment={comment}
          onDelete={() => onDeleteComment(comment.id)}
        />
      ))}
    </Stack>
    {thread.resolved_at && (
      <Stack direction="row" align="center" gap={2}>
        <Text variant="caption" tone="muted">
          Resolved by {thread.resolved_by?.full_name}
        </Text>
        <Button
          variant="ghost"
          size="xs"
          disabled={isUpdating}
          onClick={onReopen}
        >
          <ArrowCounterClockwiseIcon aria-hidden />
          Reopen
        </Button>
      </Stack>
    )}
  </Stack>
);
